import { Component } from '@theme/component';

/**
 * Bridge page countdown.
 * Reads `data-end` ISO string; updates days/hours/minutes refs every second.
 * If `data-end` is not provided, automatically targets the upcoming Monday at
 * local midnight ("Deals reset Monday" cadence).
 *
 * @typedef {object} Refs
 * @property {HTMLElement} days
 * @property {HTMLElement} hours
 * @property {HTMLElement} minutes
 *
 * @extends Component<Refs>
 */
class PvBridgeCountdown extends Component {
  requiredRefs = ['days', 'hours', 'minutes'];

  #interval = null;

  /**
   * Returns the timestamp the countdown should target.
   * - If `data-end` is set, parse it as ISO 8601.
   * - Otherwise, compute next Monday at local midnight. If today is Monday,
   *   roll forward 7 days so the countdown always represents the FULL week
   *   until the next reset, not 0 seconds.
   */
  #getEndTime() {
    const endAttr = this.getAttribute('data-end');
    if (endAttr) {
      const parsed = new Date(endAttr).getTime();
      if (!Number.isNaN(parsed)) return parsed;
    }
    const now = new Date();
    const day = now.getDay();
    const daysUntilMonday = ((8 - day) % 7) || 7;
    const target = new Date(now);
    target.setDate(now.getDate() + daysUntilMonday);
    target.setHours(0, 0, 0, 0);
    return target.getTime();
  }

  connectedCallback() {
    super.connectedCallback();

    const update = () => {
      const diff = Math.max(0, this.#getEndTime() - Date.now());
      const days = Math.floor(diff / 86400000);
      const hours = Math.floor((diff % 86400000) / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      this.refs.days.textContent = String(days).padStart(2, '0');
      this.refs.hours.textContent = String(hours).padStart(2, '0');
      this.refs.minutes.textContent = String(minutes).padStart(2, '0');
      if (diff <= 0 && this.#interval) clearInterval(this.#interval);
    };

    update();
    this.#interval = setInterval(update, 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#interval) clearInterval(this.#interval);
  }
}

if (!customElements.get('pv-bridge-countdown')) {
  customElements.define('pv-bridge-countdown', PvBridgeCountdown);
}
